import React, { useState } from "react";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";

import DefaultButton from "components/items/default-button";
import ScreenHeader from "components/items/screen-header";
import { ExcelExportIcon } from "helpers/icon-finder";
import downloadFromApi, { fetchFromApi } from "utils/download-from-api";
import formatSearchParams from "helpers/format-search-params";
import { getReportingUseHangfireQueuing } from "app/auth/store/reducers/system-configuration";
import { handleHangfireExportResponse } from "app/main/reportExport/actions/reportExport.actions";
import PatientMergeLogSearch from "./patient-access-log-search";
import { getSearchParams } from "../reducers/patientAccessLog.reducers";
import { getSignedInOrgUnit } from "../../../auth/store/reducers/user.reducer";
import { showMessage } from "../../../store/actions";

const PatientAccessLogHeader = ({ title = "Patient Access Logs" }) => {
  const dispatch = useDispatch();
  const [loadingExport, setLoadingExport] = useState(false);

  const searchParams = useSelector(getSearchParams);
  const backgroundExport = useSelector(getReportingUseHangfireQueuing);
  const signedInOrgUnit = useSelector(getSignedInOrgUnit);

  const buildExportUrl = () => {
    const timeZone = moment.tz.guess();
    const formattedParams = formatSearchParams({ ...searchParams });

    return `api/patientAccess/createExcelDocument/${signedInOrgUnit?.id}?timeZone=${timeZone}${formattedParams || searchParams}`;
  };

  const handleBackgroundExport = async exportUrl => handleHangfireExportResponse(fetchFromApi(exportUrl), dispatch);

  const handleDirectExport = async exportUrl => {
    const response = await downloadFromApi(exportUrl);
    if (!response.ok) {
      const errorMessage = await response.text();
      dispatch(showMessage({ message: errorMessage, variant: "error" }));
      return null;
    }

    return response.statusText;
  };

  const handleExport = async () => {
    setLoadingExport(true);

    try {
      const exportUrl = buildExportUrl();

      return backgroundExport
        ? await handleBackgroundExport(exportUrl)
        : await handleDirectExport(exportUrl);
    } catch (error) {
      const errorMessage = error || "An error has occurred in exporting patient access logs";
      dispatch(showMessage({ message: errorMessage, variant: "error" }));

      return errorMessage;
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <ScreenHeader
      title={title}
      renderSearch={<PatientMergeLogSearch />}
      renderActions={(
        <DefaultButton
          label="Export"
          variant="outlined"
          color="inherit"
          loading={loadingExport}
          icon={<ExcelExportIcon />}
          onClick={handleExport}
        />
      )}
    />
  );
};

export default PatientAccessLogHeader;
